import React, { useState, useRef, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase-config';

const triageTurn = httpsCallable(functions, 'maintenanceTriageTurn');

/**
 * Multi-turn AI triage before submitting a maintenance request.
 * @param {'tenant'|'owner'} props.actor
 * @param {import('firebase/auth').User} props.user
 * @param {object} props.userData - TenantsData or OwnersData shape for ids
 * @param {{ id?: string, unitNumber?: string } | null} props.selectedUnit - owner: selected unit
 * @param {(draft: { category?: string, priority?: string, location?: string, description?: string }) => void} props.onDraftMerge
 * @param {(meta: { messages: Array<{role: string, content: string, at?: string}>, aiTriageSummary: string | null }) => void} props.onSessionUpdate
 * @param {() => void} [props.onEndSessionNoRequest] - Clear draft / close flow (e.g. leave submit tab or close owner modal)
 * @param {() => void} [props.onSkipAssistantToForm] - Bypass assistant and continue directly to the form (owner override)
 * @param {boolean} [props.hideHeaderClose] - If true, omit header × (parent provides Cancel)
 * @param {boolean} [props.requireChatBeforeSkip] - Hide “skip to form” (tenant gate)
 * @param {(e: { kind: 'user_message' | 'assistant_reply' }) => void} [props.onGateMilestone]
 * @param {boolean} props.compact - tighter styles (e.g. owner modal)
 * @param {string} [props.className] - optional root class (e.g. tenant mobile layout hooks)
 */
function MaintenanceTriageChat({
  actor,
  user,
  userData,
  selectedUnit = null,
  onDraftMerge,
  onSessionUpdate,
  onEndSessionNoRequest,
  onSkipAssistantToForm,
  hideHeaderClose = false,
  requireChatBeforeSkip = false,
  onGateMilestone,
  compact = false,
  className,
}) {
  const [skipped, setSkipped] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState('triage');
  /** Scroll only this panel — avoid scrollIntoView (it scrolls the page and hides the chat). */
  const messagesScrollRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollMessagesPanelToBottom = useCallback((smooth) => {
    const el = messagesScrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      if (smooth) {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      } else {
        el.scrollTop = el.scrollHeight;
      }
    });
  }, []);

  useEffect(() => {
    scrollMessagesPanelToBottom(true);
  }, [messages, loading, scrollMessagesPanelToBottom]);

  const buildContext = useCallback(() => {
    if (actor === 'tenant') {
      return {
        actor: 'tenant',
        tenantId: user?.uid,
        ownerId: userData?.ownerId || null,
        unitId: userData?.unitId || null,
        unitNumber: userData?.unitNumber || null,
      };
    }
    return {
      actor: 'owner',
      ownerId: user?.uid,
      unitId: selectedUnit?.id || null,
      unitNumber: selectedUnit?.unitNumber != null ? String(selectedUnit.unitNumber) : null,
    };
  }, [actor, user?.uid, userData, selectedUnit]);

  const emitSession = useCallback(
    (msgs, summary) => {
      onSessionUpdate?.({
        messages: msgs.map((m) => ({
          role: m.role,
          content: m.content,
          at: m.at || new Date().toISOString(),
        })),
        aiTriageSummary: summary,
      });
    },
    [onSessionUpdate]
  );

  const sendUserMessage = async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || loading || !user?.uid) return;

    if (actor === 'owner' && !selectedUnit?.id) {
      setError('Select a unit first, then use the assistant.');
      return;
    }

    setError(null);
    setPhase((p) => (p === 'resolved' ? 'triage' : p));
    const userMsg = { role: 'user', content: trimmed, at: new Date().toISOString() };
    const nextMessages =
      messages.length === 0
        ? [userMsg]
        : [...messages, userMsg];
    setMessages(nextMessages);
    setInput('');
    onGateMilestone?.({ kind: 'user_message' });
    setLoading(true);

    try {
      const ctx = buildContext();
      const res = await triageTurn({
        messages: nextMessages.map(({ role, content }) => ({ role, content })),
        context: ctx,
      });
      const data = res.data || {};
      const assistantText = data.safetyNote
        ? `${data.safetyNote}\n\n${data.assistantMessage || ''}`.trim()
        : data.assistantMessage || 'Thanks for the details.';
      const assistantMsg = {
        role: 'assistant',
        content: assistantText,
        at: new Date().toISOString(),
      };
      const finalMessages = [...nextMessages, assistantMsg];
      setMessages(finalMessages);
      setPhase(data.phase || 'triage');
      const summary = data.aiTriageSummary || null;
      if (data.draft) {
        onDraftMerge?.(data.draft);
      }
      emitSession(finalMessages, summary);
      onGateMilestone?.({ kind: 'assistant_reply' });
    } catch (e) {
      console.error('maintenanceTriageTurn', e);
      setError(e.message || 'Could not reach the assistant. Try again or skip below.');
      const assistantMsg = {
        role: 'assistant',
        content:
          'Something went wrong on our end. You can still fill out the form and submit your request.',
        at: new Date().toISOString(),
      };
      setMessages([...nextMessages, assistantMsg]);
      onGateMilestone?.({ kind: 'assistant_reply' });
    } finally {
      setLoading(false);
      scrollMessagesPanelToBottom(false);
      requestAnimationFrame(() => {
        textareaRef.current?.focus({ preventScroll: true });
      });
    }
  };

  if (skipped) {
    return (
      <div style={{ marginBottom: compact ? 12 : 16 }}>
        <button
          type="button"
          onClick={() => setSkipped(false)}
          style={{
            background: 'none',
            border: 'none',
            color: '#6366f1',
            cursor: 'pointer',
            fontSize: compact ? 13 : 14,
            fontWeight: 600,
            textDecoration: 'underline',
            padding: 0,
          }}
        >
          Show troubleshooting assistant
        </button>
      </div>
    );
  }

  const bubbleRadius = compact ? 8 : 10;
  const fontSize = compact ? 13 : 14;

  return (
    <div
      className={className || undefined}
      style={{
        marginBottom: compact ? 16 : 24,
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        overflow: 'hidden',
        background: '#fafafa',
      }}
    >
      <div
        style={{
          padding: '12px 14px',
          background: 'linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ fontWeight: 700, color: '#111827', fontSize: compact ? 14 : 15 }}>
          Troubleshooting assistant
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {onSkipAssistantToForm && (
            <button
              type="button"
              onClick={() => {
                onSkipAssistantToForm();
                setSkipped(true);
              }}
              aria-label="Skip assistant and use form"
              title="Skip assistant"
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                border: '1px solid #e5e7eb',
                background: '#fff',
                color: '#111827',
                fontSize: 16,
                fontWeight: 800,
                lineHeight: '28px',
                cursor: 'pointer',
              }}
            >
              ×
            </button>
          )}
          {!hideHeaderClose && !onSkipAssistantToForm && onEndSessionNoRequest && (
            <button
              type="button"
              onClick={() => onEndSessionNoRequest()}
              aria-label="Cancel request"
              title="Cancel"
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                border: '1px solid #e5e7eb',
                background: '#fff',
                color: '#111827',
                fontSize: 16,
                fontWeight: 800,
                lineHeight: '28px',
                cursor: 'pointer',
              }}
            >
              ×
            </button>
          )}
          {!requireChatBeforeSkip && (
            <button
              type="button"
              onClick={() => setSkipped(true)}
              style={{
                background: 'transparent',
                border: '1px solid #c7d2fe',
                color: '#4f46e5',
                borderRadius: 6,
                padding: '4px 10px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Skip — use form only
            </button>
          )}
        </div>
      </div>
      <p
        style={{
          margin: 0,
          padding: '10px 14px',
          fontSize: 12,
          color: '#6b7280',
          lineHeight: 1.45,
          borderBottom: '1px solid #eee',
        }}
      >
        {requireChatBeforeSkip
          ? "Tip: mention the room and what you noticed (e.g. kitchen — dishwasher won't start). Not for emergencies — call 911 or your utility."
          : "Describe the issue; we'll ask a few quick questions. For emergencies, call 911 or your utility."}
      </p>

      <div
        ref={messagesScrollRef}
        data-triage-messages-scroll
        style={{
          maxHeight: compact ? 220 : 280,
          overflowY: 'auto',
          padding: '12px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {messages.length === 0 && (
          <div style={{ fontSize, color: '#9ca3af', fontStyle: 'italic' }}>
            Example: “The outlet in my kitchen stopped working.”
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            style={{
              alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '92%',
              padding: '10px 12px',
              borderRadius: bubbleRadius,
              fontSize,
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              background: m.role === 'user' ? '#6366f1' : '#fff',
              color: m.role === 'user' ? '#fff' : '#1f2937',
              border: m.role === 'assistant' ? '1px solid #e5e7eb' : 'none',
            }}
          >
            {m.content}
          </div>
        ))}
        {loading && (
          <div style={{ fontSize: 12, color: '#6b7280', paddingLeft: 4 }}>Thinking…</div>
        )}
      </div>

      {phase === 'suggest_submit' && (
        <div
          style={{
            margin: '10px 14px 8px',
            padding: '8px 12px',
            background: '#ecfdf5',
            border: '1px solid #a7f3d0',
            borderRadius: 8,
            fontSize: 12,
            color: '#065f46',
            fontWeight: 600,
          }}
        >
          Ready to submit? Review the form below, add photos if you can, then submit the request.
        </div>
      )}
      {phase === 'resolved' && (
        <div
          style={{
            margin: '10px 14px 8px',
            padding: '12px 14px',
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: 8,
            fontSize: 13,
            color: '#1e3a8a',
            lineHeight: 1.5,
          }}
        >
          <p style={{ margin: '0 0 10px 0', fontWeight: 700 }}>
            Sounds like you are all set — no maintenance request is needed unless something acts up again.
          </p>
          <p style={{ margin: '0 0 12px 0', fontSize: 12, color: '#1e40af', fontWeight: 500 }}>
            End the session to close this request — your draft is cleared and nothing is sent to maintenance. You can
            start a new request anytime if the issue returns.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button
              type="button"
              onClick={() => {
                onSessionUpdate?.({ messages: [], aiTriageSummary: null });
                if (onEndSessionNoRequest) {
                  onEndSessionNoRequest();
                  return;
                }
                setMessages([]);
                setPhase('triage');
                setInput('');
                setError(null);
                setSkipped(true);
              }}
              style={{
                padding: '8px 14px',
                background: '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              End session — no request
            </button>
            <button
              type="button"
              onClick={() => setPhase('triage')}
              style={{
                padding: '8px 14px',
                background: '#fff',
                color: '#1e40af',
                border: '1px solid #93c5fd',
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Not fully fixed — keep chatting
            </button>
          </div>
        </div>
      )}

      {error && (
        <div
          style={{
            margin: '0 14px 8px',
            padding: '8px 10px',
            background: '#fef2f2',
            color: '#991b1b',
            borderRadius: 8,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          padding: '10px 14px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              e.stopPropagation();
              sendUserMessage(input);
            }
          }}
          placeholder={
            phase === 'resolved'
              ? 'Still an issue? Describe it here… (Enter to send)'
              : 'Type your message… (Enter to send, Shift+Enter for newline)'
          }
          rows={compact ? 2 : 3}
          disabled={loading}
          style={{
            width: '100%',
            resize: 'vertical',
            minHeight: compact ? 44 : 56,
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid #d1d5db',
            fontFamily: 'inherit',
            fontSize,
            boxSizing: 'border-box',
          }}
        />
        <button
          type="button"
          onClick={() => sendUserMessage(input)}
          disabled={loading || !input.trim()}
          style={{
            width: '100%',
            padding: '10px 16px',
            background: loading || !input.trim() ? '#c4b5fd' : '#6366f1',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontWeight: 700,
            cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
            fontSize: 13,
          }}
        >
          Send
        </button>
      </div>
    </div>
  );
}

export default MaintenanceTriageChat;
